<template>
    <slot>
        <div class="receipt-panel">
        <div class="d-flex align-items-center justify-content-between receipt-panel-head">
            <label class="form-label mb-0">Receipt Details</label>
            <a href="#" class="add-new-mop" @click.prevent="addReceiptRow">
                <i class="fas fa-plus"></i> Add New
            </a>
        </div>
        <div class="table-responsive">
            <table class="table table-bordered mop-table mb-0">
                <thead>
                    <tr>
                        <th class="mop-mode">Mode of Payment</th>
                        <th class="mop-amount text-right">Receipt Amount</th>
                        <th class="mop-ref">Receipt Referance</th>
                        <th class="mop-note">Note</th>
                        <th class="mop-action"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, i) in formData.mop_receipts" :key="i">
                        <td>
                            <v-select
                                placeholder="Select Cash & Bank Account"
                                class="w-100 mop-select"
                                v-model="row.mop_account_head_id"
                                :options="cashAndBank"
                                label="name"
                                :reduce="name => name.id"
                                append-to-body
                                :calculate-position="withPopper"
                                @option:selected="option => onModeSelected(option, i)"
                            />
                        </td>
                        <td>
                            <input
                                v-model.number="row.receipt_amount"
                                type="number"
                                class="form-control form-control-sm text-right"
                                placeholder="0.00"
                                :min="0"
                                @input="onRowAmountChange"
                            >
                        </td>
                        <td>
                            <input
                                v-model.trim="row.receipt_reference"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Reference"
                            >
                        </td>
                        <td>
                            <input
                                v-model.trim="row.note"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Note"
                            >
                        </td>
                        <td class="text-center">
                            <button
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                @click="removeReceiptRow(i)"
                                :disabled="formData.mop_receipts.length < 2"
                            >
                                <i class="fas fa-trash"></i>
                            </button>
                        </td>
                    </tr>
                    <tr class="mop-total-row">
                        <td class="text-bold">TOTAL</td>
                        <td class="text-right text-bold mop-amount-cell">{{ totalReceipt.toFixed(2) }}</td>
                        <td colspan="3"></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="row receipt-panel-foot">
            <div class="col-12 col-md-6">
                <label class="form-label" for="netPayable">Net Payable</label>
                <input
                    :value="props.grandTotal"
                    type="number"
                    class="form-control text-right net-payable-input"
                    disabled
                >
            </div>
            <div class="col-12 col-md-6 d-flex align-items-end">
                <div class="due-chip" :class="dueAmount > 0 ? 'due-open' : 'due-clear'">
                    <span class="due-label">Due</span>
                    <span class="due-value">{{ dueAmount.toFixed(2) }} BDT</span>
                </div>
            </div>
        </div>
        </div>
    </slot>
</template>

<script setup>
import {computed, inject, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {createPopper} from "@popperjs/core";
import handleReceipt from "@/services/modules/receipt";

const $route = useRoute();
const {fetchCashAndBank} = handleReceipt();
const formData = inject('formData')
const showError = inject('showError')
const props = defineProps({
    grandTotal: {
        type: Number
    },
    isHospitalInvoice: {
        type: Boolean,
        default: false
    },
    autoFillPayment: {
        type: Boolean,
        default: true
    }
})

const companyId = $route.params.companyId;
const cashAndBank = ref([])

const withPopper = (dropdownList, component, {width}) => {
    dropdownList.style.width = width;
    const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom',
        modifiers: [
            {name: 'offset', options: {offset: [0, 2]}},
            {
                name: 'toggleClass',
                enabled: true,
                phase: 'write',
                fn({state}) {
                    component.$el.classList.toggle('drop-up', state.placement === 'top');
                }
            }
        ]
    });
    return () => popper.destroy();
}

const emptyRow = () => {
    return {
        receipt_master_id: null,
        mop_account_head_id: null,
        receipt_amount: null,
        receipt_reference: '',
        note: ''
    }
}

const ensureRows = () => {
    if (!formData.value.mop_receipts) formData.value.mop_receipts = [];
    if (!formData.value.mop_receipts.length) formData.value.mop_receipts.push(emptyRow());
}

const totalReceipt = computed(() => {
    if (!formData.value.mop_receipts) return 0;
    return formData.value.mop_receipts
        .map(row => parseFloat(row.receipt_amount) || 0)
        .reduce((acc, val) => acc + val, 0);
})

const dueAmount = computed(() => {
    const payable = parseFloat(props.grandTotal) || 0;
    return payable - totalReceipt.value;
})

const syncPayment = () => {
    formData.value.payment = totalReceipt.value;
    const firstRow = formData.value.mop_receipts[0];
    formData.value.mop_account_head_id = firstRow ? firstRow.mop_account_head_id : null;
    formData.value.receipt_reference = firstRow ? firstRow.receipt_reference : null;
}

const addReceiptRow = () => {
    formData.value.mop_receipts.push(emptyRow());
}

const onModeSelected = (option, index) => {
    if (!option || !option.id) return;
    const duplicate = formData.value.mop_receipts.some((row, i) => i !== index && row.mop_account_head_id === option.id);
    if (!duplicate) return;
    showError("This mode of payment is already selected!");
    formData.value.mop_receipts[index].mop_account_head_id = null;
}

const removeReceiptRow = (index) => {
    if (formData.value.mop_receipts.length < 2) return;
    formData.value.mop_receipts.splice(index, 1);
    syncPayment();
}

const onRowAmountChange = () => {
    if (totalReceipt.value > parseFloat(props.grandTotal)) {
        showError("Total receipt cannot be greater than net payable");
    }
    syncPayment();
}

const setInitialMop = () => {
    if (!cashAndBank.value.length) return;
    const firstRow = formData.value.mop_receipts[0];
    if (firstRow && !firstRow.mop_account_head_id) {
        firstRow.mop_account_head_id = cashAndBank.value[0].id;
    }
    syncPayment();
}

watch(totalReceipt, () => syncPayment())

const generateMoneyReceiptNumber = async () => {
    formData.value.mop_receipts = [emptyRow()];
    setInitialMop();
}

onMounted(() => {
    ensureRows();
    Promise.all([
        fetchCashAndBank(companyId).then(res => {
            if (res.data) cashAndBank.value = res.data
        }),
    ]).then(() => {
        setInitialMop();
    })
    .catch(e => {})
})

defineExpose({generateMoneyReceiptNumber})
</script>

<style scoped>
.receipt-panel {
    --rp-line: #ebe9f1;
    --rp-head-bg: #f3f2f7;
    --rp-head-ink: #6e6b7b;
    --rp-hover: #f8f8fb;
    --rp-card: #ffffff;
    --rp-ink: #5e5873;
    --rp-primary: #7367f0;
    --rp-total-bg: #eeecff;
    background: var(--rp-card);
    border: 1px solid var(--rp-line);
    border-radius: 10px;
    overflow: hidden;
}
.receipt-panel-head {
    padding: 12px 14px;
    background: linear-gradient(90deg, var(--rp-total-bg), transparent 70%);
    border-bottom: 1px solid var(--rp-line);
}
.receipt-panel-head .form-label {
    font-weight: 600;
    color: var(--rp-ink);
}
.add-new-mop {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--rp-primary);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border: 1px solid var(--rp-primary);
    border-radius: 6px;
    transition: background 0.15s ease, color 0.15s ease;
}
.add-new-mop:hover {
    background: var(--rp-primary);
    color: #fff;
}
.add-new-mop i {
    font-size: 0.7rem;
}
.mop-table {
    margin-bottom: 0;
}
.mop-table th {
    font-size: 0.72rem;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    vertical-align: middle;
    background: var(--rp-head-bg);
    color: var(--rp-head-ink);
    border-color: var(--rp-line);
    font-weight: 600;
}
.mop-table td {
    vertical-align: middle;
    border-color: var(--rp-line);
    padding: 6px 8px;
}
.mop-table th {
    padding: 8px;
}
.mop-table .form-control-sm {
    height: 32px;
}
.mop-table tbody tr:not(.mop-total-row):hover {
    background: var(--rp-hover);
}
.mop-mode {
    width: 28%;
}
.mop-amount {
    width: 20%;
}
.mop-ref {
    width: 24%;
}
.mop-note {
    width: 24%;
}
.mop-action {
    width: 4%;
}
.mop-amount-cell {
    font-variant-numeric: tabular-nums;
}
.mop-table .btn-outline-danger {
    border-color: rgba(234, 84, 85, 0.4);
    color: #ea5455;
    padding: 4px 9px;
    transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.mop-table .btn-outline-danger:hover:not(:disabled) {
    border-color: #ea5455;
    color: #fff;
    background: #ea5455;
}
.mop-table .btn-outline-danger:disabled {
    opacity: 0.4;
    border-color: var(--rp-line);
}
.text-bold {
    font-weight: bold;
}
.mop-total-row td {
    background: var(--rp-card);
    color: var(--rp-ink);
    font-size: 0.95rem;
    border-top: 1px solid var(--rp-line);
    border-bottom: 0;
}
.mop-total-row td:not(:first-child) {
    border-left: 0;
}
.receipt-panel-foot {
    padding: 14px;
    margin: 0;
    row-gap: 12px;
}
.net-payable-input {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
}
.due-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    width: 100%;
    justify-content: flex-end;
}
.due-chip .due-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
    opacity: 0.8;
}
.due-chip .due-value {
    font-size: 1.05rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}
.due-open {
    background: rgba(234, 84, 85, 0.12);
    color: #ea5455;
}
.due-clear {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
}
</style>

<style>
.v-select.mop-select.drop-up .vs__dropdown-toggle {
    border-radius: 0 0 4px 4px;
}
.mop-select .vs__dropdown-menu {
    z-index: 1055;
}
</style>
